import { DialogTitle } from "@mui/material";
import { orderBusiness } from "api/bussiness/order/order-bussiness";
import { packingNoteBusiness } from "api/bussiness/package-note/packing-note-business";
import { workOrderPoBusiness } from "api/bussiness/work-order-po/work-order-po-bussiness";
import { dateTimeToUTCString } from "commons/ultilities/date/dayjs";
import { DynamicForm } from "commons/ultilities/dynamic-form/components/dynamic-form";
import { DynamicFormArray } from "commons/ultilities/dynamic-form/components/dynamic-form-array";
import { useForm } from "commons/ultilities/dynamic-form/hooks/use-form";
import {
  IControl,
  IControlProperties,
} from "commons/ultilities/dynamic-form/models/control";
import { createProductOptionLabel } from "commons/ultilities/option-item/label-ultil";
import dayjs from "dayjs";
import { cloneDeep } from "lodash";
import { observer } from "mobx-react-lite";
import { IOrderProperties } from "mobx-stores/_models/orders/order";
import { IAddPackingNote } from "mobx-stores/_models/packing-note/packing-note";
import { IProductProperties } from "mobx-stores/_models/products/product";
import { IWorkOrderPoProperties } from "mobx-stores/_models/work-order-po/work-order-po";
import { useStores } from "mobx-stores/_root-store";
import { useCallback, useMemo } from "react";
import { DialogComplexContent } from "share-components/templates/form/dialog-complex-form/dialog-complex-content";
import { DialogComplexFormContainer } from "share-components/templates/form/dialog-complex-form/dialog-complex-form";
import { DialogComplexFormArrayContainer } from "share-components/templates/form/dialog-complex-form/dialog-complex-form-array";
import { DialogForm } from "share-components/templates/form/dialog-form";
import { getAddPackingNoteFormSchema } from "../schema";

export const AddPackingNote = observer(() => {
  const {
    packingNoteManagement: { openNew, getPackingNotes, setProp },
  } = useStores();
  const {
    form: { controls, isValid },
    getControl,
    getControlArray,
    submitHandler,
  } = useForm<IAddPackingNote>((form) => getAddPackingNoteFormSchema(form), {
    date: dateTimeToUTCString(dayjs()),
    number: "",
    customerId: "",
    purchaseOrderId: "",
    kcsPackage: "",
    kcsTest: "",
    workOrderPOId: "",
    items: [
      {
        amount: 0,
        boxNumber: "",
        grossWeight: undefined,
        netWeight: undefined,
        orderNumber: undefined,
        productId: "",
      },
    ],
  });

  const saveHandler = useCallback(
    async (data: IAddPackingNote) => {
      try {
        await packingNoteBusiness.add(data);
        await getPackingNotes();
        setProp("openNew", false);
      } catch (ex) {
        console.error(ex);
      }
    },
    [getPackingNotes, setProp]
  );

  const itemControl = useMemo(() => {
    return getControl("items");
  }, [getControl]);

  const packingNoteItems = useMemo(() => {
    return getControlArray("items");
  }, [getControlArray]);

  const removeRowHandler = useCallback(
    (idx: number) => {
      itemControl.removeChildByIndex(idx);
    },
    [itemControl]
  );

  const addNewPackingNoteItemRow = useCallback(() => {
    itemControl.addNewChild();
  }, [itemControl]);

  const autoFillCustomerByPurchaseOrder = useCallback(
    (purchaseOrder: IOrderProperties) => {
      const customerIdCtr = getControl("customerId");
      if (!customerIdCtr) return;
      customerIdCtr.setValueAndValidate(purchaseOrder.customer?.id);
      customerIdCtr.setProp("optionItems", [
        {
          label: purchaseOrder.customer?.dealingName || "",
          value: purchaseOrder.customer?.id,
        },
      ]);
    },
    [getControl]
  );

  const setOptionItemsForProduct = useCallback(
    (wo: IWorkOrderPoProperties) => {
      if (itemControl) {
        const newChildSchema = cloneDeep(itemControl.childSchema);
        const productIdSchema = newChildSchema.productId as IControlProperties;
        const product = wo.purchaseOrderItem?.product;
        productIdSchema.optionItems = product
          ? [
              {
                label: createProductOptionLabel(product as IProductProperties),
                value: product.id,
              },
            ]
          : [];

        itemControl.setChildSchema(newChildSchema);
      }
    },
    [itemControl]
  );

  const setOptionItemsForProductIsEmty = useCallback(() => {
    if (itemControl) {
      const newChildSchema = cloneDeep(itemControl.childSchema);
      const productIdSchema = newChildSchema.productId as IControlProperties;
      productIdSchema.optionItems = [];

      itemControl.setChildSchema(newChildSchema);
    }
  }, [itemControl]);

  const cleanItems = useCallback(() => {
    if (itemControl) {
      itemControl.setProp("children", []);
    }
  }, [itemControl]);

  const cleanWOValue = useCallback(() => {
    const woCtr = getControl("workOrderPOId");
    if (woCtr) {
      woCtr.setDefaultValue(null);
    }
  }, [getControl]);

  const valueChangeHandler = useCallback(
    async (control: IControl) => {
      if (control.name === "purchaseOrderId") {
        if (control.value) {
          const purchaseOrderDetail = await orderBusiness.getById(
            control.value
          );
          autoFillCustomerByPurchaseOrder(purchaseOrderDetail);
          cleanWOValue();
          cleanItems();
          setOptionItemsForProductIsEmty();
          itemControl.addNewChild();
        }
      }

      if (control.name === "workOrderPOId") {
        if (control.value) {
          cleanItems();
          setOptionItemsForProductIsEmty();
          const wo = await workOrderPoBusiness.getById(control.value);
          setOptionItemsForProduct(wo);
          itemControl.addNewChild();
        }
      }
    },
    [
      autoFillCustomerByPurchaseOrder,
      cleanItems,
      cleanWOValue,
      itemControl,
      setOptionItemsForProduct,
      setOptionItemsForProductIsEmty,
    ]
  );

  return (
    <DialogForm
      open={openNew}
      fullWidth
      maxWidth={"xl"}
      onClose={() => setProp("openNew", false)}
      onCancelBtnClicked={() => setProp("openNew", false)}
      onSaveBtnClick={async () => await submitHandler(saveHandler)}
      saveBtnProps={{
        disabled: !isValid,
      }}
    >
      <DialogTitle>Thêm phiếu đóng gói</DialogTitle>
      <DialogComplexContent>
        <DialogComplexFormContainer>
          <DynamicForm
            controls={controls}
            onValueChanged={valueChangeHandler}
          ></DynamicForm>
        </DialogComplexFormContainer>
        <DialogComplexFormArrayContainer
          label={itemControl.label}
          addNewRow={addNewPackingNoteItemRow}
        >
          {packingNoteItems && packingNoteItems.length > 0 && (
            <DynamicFormArray
              containerProps={{
                sx: {
                  marginTop: "10px",
                  marginBottom: "10px",
                },
              }}
              children={packingNoteItems}
              onRemoveRowClick={removeRowHandler}
            ></DynamicFormArray>
          )}
        </DialogComplexFormArrayContainer>
      </DialogComplexContent>
    </DialogForm>
  );
});
